import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";

import ConfirmDialog from "components/common/ConfirmDialog";
import { pushNotification } from "features/notificationSlice";
import {
  useDeleteSecureFilesMutation,
  useGetSecureFileAccessUrlMutation,
  useGetSecureFilesAccessUrlsMutation,
  usePurgeSecureFilesMutation,
  useRestoreSecureFilesMutation,
} from "features/secureUploads/secureUploads";
import { downloadZipFromPresignedEntries } from "utils/browserZipDownload";
import { formatApiError } from "utils/formatApiError";

import MoveFilesDialog, { type MoveRequest } from "./MoveFilesDialog";
import {
  type BulkActionKey,
  type SelectedFile,
  restoreGroups,
  uniqueNames,
  zipName,
} from "./selectionModel";

const MOVES: Partial<Record<BulkActionKey, Omit<MoveRequest, "files">>> = {
  add_to_project: { mode: "project", title: "Add to Project" },
  move_to_project: { mode: "project", title: "Move to Project" },
  add_to_collection: { mode: "collection", title: "Add to Collection" },
  move_files: { mode: "collection", title: "Move Files" },
};

const count = (files: SelectedFile[] | null) =>
  files?.length === 1 ? files[0].file_name : `${files?.length ?? 0} files`;

/** What the selection bar does with the selected files, and its dialogs.
 * `onDone` hears of files that left the list; `onGone` of a selection that no
 * longer matches what is there (someone else deleted or moved a file). */
export function useBulkFileActions({
  clientId,
  clientName,
  place,
  onDone,
  onGone,
}: {
  clientId: number;
  clientName: string;
  /** Where the files were picked, after the client - it names the zip. */
  place: string[];
  onDone: (ids: number[]) => void;
  onGone: () => void;
}) {
  const dispatch = useDispatch();
  const [getLinks] = useGetSecureFilesAccessUrlsMutation();
  const [getLink] = useGetSecureFileAccessUrlMutation();
  const [deleteFiles, { isLoading: deleting }] = useDeleteSecureFilesMutation();
  const [restoreFiles, { isLoading: restoring }] =
    useRestoreSecureFilesMutation();
  const [purgeFiles, { isLoading: purging }] = usePurgeSecureFilesMutation();
  const [toDelete, setToDelete] = useState<SelectedFile[] | null>(null);
  const [toRestore, setToRestore] = useState<SelectedFile[] | null>(null);
  const [toPurge, setToPurge] = useState<SelectedFile[] | null>(null);
  const [toMove, setToMove] = useState<MoveRequest | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const toast = (message: string, severity: "success" | "error" | "info") =>
    dispatch(pushNotification({ message, severity }));

  const refused = (error: unknown, fallback: string) => {
    const response = error as { status?: number; data?: unknown };
    if (response?.status === 404) {
      toast(
        "Some of these files are no longer here. Select them again.",
        "error",
      );
      onGone();
      return;
    }
    toast(formatApiError(response?.data, fallback), "error");
  };

  const download = async (files: SelectedFile[]) => {
    if (progress) return;
    try {
      if (files.length === 1) {
        const { url } = await getLink({ id: files[0].id }).unwrap();
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      setProgress("Preparing the download...");
      const links = await getLinks(files.map((file) => file.id)).unwrap();
      const names = uniqueNames(links.map((link) => link.file_name));
      const result = await downloadZipFromPresignedEntries(
        links.map((link, index) => ({
          url: link.url,
          relative_path: names[index],
        })),
        zipName([clientName, ...place], new Date()),
        (done, total) => setProgress(`Downloading ${done} of ${total}...`),
      );
      if (!result.ok) toast("The files could not be downloaded.", "error");
    } catch (error) {
      refused(error, "The files could not be downloaded.");
    } finally {
      setProgress(null);
    }
  };

  /** Runs a confirmed action; the dialog closes either way. */
  const confirm = async (
    files: SelectedFile[] | null,
    close: () => void,
    act: (ids: number[]) => Promise<unknown>,
    done: (ids: number[]) => string,
    fallback: string,
  ) => {
    if (!files) return;
    const ids = files.map((file) => file.id);
    try {
      await act(ids);
      toast(done(ids), "success");
      close();
      onDone(ids);
    } catch (error) {
      close();
      refused(error, fallback);
    }
  };

  const run = (action: BulkActionKey, files: SelectedFile[]) => {
    const moving = MOVES[action];
    if (action === "download") void download(files);
    else if (action === "delete") setToDelete(files);
    else if (action === "restore") setToRestore(files);
    else if (action === "purge") setToPurge(files);
    else if (moving)
      setToMove({
        ...moving,
        title:
          action === "move_files" && files.length === 1
            ? "Move File"
            : moving.title,
        files,
      });
  };

  const groups = toRestore ? restoreGroups(toRestore) : [];
  const dialogs = (
    <>
      <ConfirmDialog
        open={toDelete !== null}
        title={toDelete?.length === 1 ? "Delete File" : "Delete Files"}
        message={
          <Typography variant="body2">
            <b>{count(toDelete)}</b> will be removed for everyone who can see{" "}
            {toDelete?.length === 1 ? "it" : "them"}. An Admin can restore{" "}
            {toDelete?.length === 1 ? "it" : "them"} from Deleted Files.
          </Typography>
        }
        confirmLabel={
          toDelete?.length === 1
            ? "Delete File"
            : `Delete ${toDelete?.length} Files`
        }
        onConfirm={() =>
          confirm(
            toDelete,
            () => setToDelete(null),
            (ids) => deleteFiles(ids).unwrap(),
            (ids) =>
              ids.length === 1
                ? "File deleted."
                : `${ids.length} files deleted.`,
            "The files could not be deleted.",
          )
        }
        onCancel={() => setToDelete(null)}
        loading={deleting}
      />
      <ConfirmDialog
        open={toRestore !== null}
        title={toRestore?.length === 1 ? "Restore File" : "Restore Files"}
        message={
          <>
            <Typography variant="body2">
              <b>{count(toRestore)}</b>{" "}
              {toRestore?.length === 1 ? "goes" : "go"} back where{" "}
              {toRestore?.length === 1 ? "it was" : "they were"} deleted from:
            </Typography>
            <Box component="ul" sx={{ my: 1, pl: 3 }}>
              {groups.map((group) => (
                <Typography component="li" variant="body2" key={group.label}>
                  <b>{group.label}</b> -{" "}
                  {group.count === 1 ? "1 file" : `${group.count} files`}
                </Typography>
              ))}
            </Box>
            {groups.some((group) => group.individual) ? (
              <Typography variant="body2">
                In Individual Files everyone at {toRestore?.[0]?.client_name}{" "}
                with Secure Exchange access sees a file - a Confidential one
                only with Confidential Access, a first-version one only its
                sender and the people it is shared with.
              </Typography>
            ) : null}
          </>
        }
        confirmLabel={
          toRestore?.length === 1
            ? "Restore"
            : `Restore ${toRestore?.length} Files`
        }
        onConfirm={() =>
          confirm(
            toRestore,
            () => setToRestore(null),
            (ids) => restoreFiles(ids).unwrap(),
            (ids) =>
              ids.length === 1
                ? "File restored."
                : `${ids.length} files restored.`,
            "The files could not be restored.",
          )
        }
        onCancel={() => setToRestore(null)}
        loading={restoring}
      />
      <ConfirmDialog
        open={toPurge !== null}
        title="Delete Permanently"
        message={
          <Typography variant="body2">
            <b>{count(toPurge)}</b> will be deleted for good.{" "}
            {toPurge?.length === 1 ? "It" : "They"} cannot be restored.
          </Typography>
        }
        confirmLabel={
          toPurge?.length === 1
            ? "Delete Permanently"
            : `Delete ${toPurge?.length} Files Permanently`
        }
        onConfirm={() =>
          confirm(
            toPurge,
            () => setToPurge(null),
            (ids) => purgeFiles(ids).unwrap(),
            (ids) =>
              ids.length === 1
                ? "File deleted permanently."
                : `${ids.length} files deleted permanently.`,
            "The files could not be deleted.",
          )
        }
        onCancel={() => setToPurge(null)}
        loading={purging}
      />
      <MoveFilesDialog
        request={toMove}
        clientId={clientId}
        onClose={() => setToMove(null)}
        onMoved={onDone}
      />
    </>
  );

  return {
    run,
    dialogs,
    progress,
  };
}
